// Complaint traceability, phase 1 (rule 36): the D365 batch this case is about, resolved when it
// arrived, and what is known about that batch, so QA opens the case with the answer instead of
// looking it up in D365. A suggestion until a person names it: an inferred batch says so.
//
// Rule 15 throughout: a read that failed says "not known" and why, never "none".
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api } from '../../lib/api';
import { fieldClass, labelClass, officeFocus, serverMessage, useResource } from '../../shells/OfficeShell';
import { day } from './complaintsModel';

export interface FgBatch {
  item: string;
  itemName: string | null;
  batch: string;
  made: string | null;
  bestBefore: string | null;
}

export type BatchState = 'EXACT' | 'INFERRED' | 'CANDIDATES' | 'NOT_FOUND' | 'UNREADABLE' | 'NOT_KNOWN' | 'NO_CODE';

export interface BatchView {
  state: BatchState;
  source: 'AUTO' | 'PERSON';
  item: string | null;
  itemName: string | null;
  batch: string | null;
  made: string | null;
  bestBefore: string | null;
  candidates: FgBatch[];
  reason: string;
  byUpn: string | null;
  at: string;
}

interface Facts {
  batch: string;
  item: string;
  disposition: { code: string } | { missing: true } | { unknown: string };
  movements: {
    asOf: string | null;
    unit: string | null;
    itemName: string | null;
    made: { qty: number; first: string | null } | null;
    sold: { qty: number; lines: number; first: string | null; last: string | null } | null;
    left: number | null;
  } | null;
  movementsUnknown: string | null;
  daysLeftAtComplaint: number | null;
  holds: Array<{ holdId: number; state: string; reason: string | null }>;
  ncrs: Array<{ ncrId: string; raisedByUpn: string | null; at: string | null }>;
  /** Absent from an older API; null = D365 NCRs not read. */
  d365Ncrs?: Array<{ ncr: string; problem: string | null; closed: boolean; date: string | null; type: string | null }> | null;
  d365NcrsUnknown?: string | null;
  otherCases: number[];
}

const card = 'flex flex-col gap-(--s3) rounded-md border border-(--border) bg-(--card) p-(--s4)';
const caseNo = (id: number) => `CMP-${String(id).padStart(4, '0')}`;
const qty = (n: number, unit: string | null) => `${n.toLocaleString('en-AU')}${unit ? ` ${unit.toLowerCase()}` : ''}`;

/** One short line for the register and the phone card: the batch, or why there is none. */
export function batchLine(b: BatchView | null | undefined): { text: string; tone: 'ok' | 'warn' | 'quiet' } | null {
  if (!b) return null;
  switch (b.state) {
    case 'EXACT':
      return { text: `D365 ${b.batch}${b.source === 'PERSON' ? ' (named)' : ''}`, tone: 'ok' };
    case 'INFERRED':
      return { text: `D365 ${b.batch} (inferred)`, tone: 'ok' };
    case 'CANDIDATES':
      return { text: `${b.candidates.length} possible batches`, tone: 'warn' };
    case 'NOT_FOUND':
      return { text: 'no D365 batch found', tone: 'warn' };
    case 'NOT_KNOWN':
      return { text: 'D365 not read yet', tone: 'warn' };
    case 'UNREADABLE':
      return { text: 'code not recognised', tone: 'quiet' };
    default:
      return null;
  }
}

const STATE_WORDS: Record<BatchState, string> = {
  EXACT: 'Printed on the pack',
  INFERRED: 'Inferred from the date code',
  CANDIDATES: 'More than one batch fits',
  NOT_FOUND: 'Not found in D365',
  UNREADABLE: 'Code not recognised',
  NOT_KNOWN: 'D365 not read',
  NO_CODE: 'No code given',
};

export function ComplaintBatch({ caseId, canName, closed, initial }: { caseId: number; canName: boolean; closed: boolean; initial?: BatchView | null }) {
  // The resolution is in the case payload already (`initial`): show it at once, and the live facts (D365,
  // stock, holds, NCRs: ~15 s) when they come (UI/UX pass, 3 Nov 2026: "Reading the batch…" for 15 s on a phone).
  const res = useResource<{ batch: BatchView | null; facts: Facts | null; note?: string }>(`/complaints/${caseId}/batch`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [other, setOther] = useState({ item: '', batch: '' });
  const [showOther, setShowOther] = useState(false);

  const name = async (item: string, batch: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/complaints/${caseId}/batch`, { item, batch });
      setShowOther(false);
      res.reload();
    } catch (e) {
      setError(serverMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const head = (
    <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
      <h2 className="m-0 text-(length:--fs-sm) font-semibold">The batch</h2>
      <span className="text-[12px] text-(--muted)">read from D365 when the complaint arrived</span>
    </div>
  );

  if (res.error) {
    return (
      <section className={card} aria-label="The batch">
        {head}
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read the batch: {res.error}. This is not the same as "no batch".</p>
          <Button variant="outline" size="sm" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      </section>
    );
  }
  if (!res.data && !initial) {
    return (
      <section className={card} aria-label="The batch">
        {head}
        <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">Reading the batch…</p>
      </section>
    );
  }
  const b = res.data ? res.data.batch : (initial ?? null);
  const f = res.data?.facts ?? null;
  const factsLoading = !res.data && !!initial?.batch;
  const pickable = canName && !closed;
  const m = f?.movements ?? null;

  return (
    <section className={card} aria-label="The batch">
      {head}
      {!b ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">{res.data?.note ?? 'Not resolved yet: the mirror resolves a new case within 10 minutes.'}</p>
      ) : (
        <>
          <div className="flex flex-col gap-(--s1)">
            {b.batch ? (
              <p className="m-0 text-(length:--fs-md)">
                <span className="font-semibold tabular-nums">Batch {b.batch}</span>
                {' · '}
                {m?.itemName ?? b.itemName ?? 'item'} ({b.item})
              </p>
            ) : null}
            <p className="m-0 text-(length:--fs-sm)">
              <span className={`mr-(--s2) rounded-full px-2 py-0.5 text-[11px] font-semibold ${b.state === 'EXACT' ? 'bg-(--in-bg) text-(--in-fg)' : b.state === 'INFERRED' ? 'bg-(--action-bg) text-(--action-fg)' : 'bg-(--subtle) text-(--muted)'}`}>
                {b.source === 'PERSON' ? `Named by ${b.byUpn}` : STATE_WORDS[b.state]}
              </span>
              <span className="text-(--muted)">{b.reason}</span>
            </p>
          </div>

          {factsLoading ? (
            <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">
              Reading D365, stock and NCRs for this batch…
            </p>
          ) : null}
          {f ? (
            <dl className="m-0 grid grid-cols-1 gap-x-(--s4) gap-y-(--s2) text-(length:--fs-sm) sm:grid-cols-[11rem_1fr]">
              <dt className="font-semibold">Made · best before</dt>
              <dd className="m-0">
                {b.made ? day(b.made) : 'not known'} · {b.bestBefore ? day(b.bestBefore) : 'not known'}
                {f.daysLeftAtComplaint !== null ? (
                  <span className={f.daysLeftAtComplaint < 0 ? 'font-semibold text-(--out-fg)' : 'text-(--muted)'}>
                    {' '}
                    ({f.daysLeftAtComplaint < 0 ? `${-f.daysLeftAtComplaint} days past best-before` : `${f.daysLeftAtComplaint} days left`} when the consumer wrote)
                  </span>
                ) : null}
              </dd>
              <dt className="font-semibold">Disposition now</dt>
              <dd className="m-0">
                {'code' in f.disposition ? (
                  <span className={f.disposition.code !== 'Available' ? 'font-semibold text-(--warn)' : ''}>{f.disposition.code || 'none'}</span>
                ) : 'missing' in f.disposition ? (
                  'D365 no longer finds this batch'
                ) : (
                  <span className="text-(--warn)">not known ({f.disposition.unknown})</span>
                )}
              </dd>
              <dt className="font-semibold">Made and shipped</dt>
              <dd className="m-0">
                {m ? (
                  <>
                    {m.made ? `${qty(m.made.qty, m.unit)} made${m.made.first ? ` ${day(m.made.first)}` : ''}` : 'no production recorded'}
                    {m.sold ? ` · ${qty(m.sold.qty, m.unit)} sold on ${m.sold.lines} order lines${m.sold.first ? `, ${day(m.sold.first)} to ${day(m.sold.last ?? m.sold.first)}` : ''}` : ' · none sold'}
                    {m.left !== null ? ` · ${m.left > 0 ? `${qty(m.left, m.unit)} still on hand` : 'none on hand'}` : ''}
                    {m.asOf ? <span className="block text-[11px] text-(--muted)">INVENTORYV2 as refreshed {m.asOf.slice(0, 16).replace('T', ' ')} UTC</span> : null}
                  </>
                ) : (
                  <span className="text-(--warn)">not known ({f.movementsUnknown})</span>
                )}
              </dd>
              <dt className="font-semibold">QC holds · NCRs</dt>
              <dd className="m-0">
                {f.holds.length ? f.holds.map((h) => `Hold #${h.holdId} ${h.state.toLowerCase()}`).join(', ') : 'no open QC hold'}
                {' · '}
                {f.d365Ncrs === undefined ? (
                  f.ncrs.length ? f.ncrs.map((n) => `NCR ${n.ncrId}`).join(', ') : 'no NCR raised from the app'
                ) : f.d365Ncrs === null ? (
                  <span className="text-(--warn)">D365 NCRs not known ({f.d365NcrsUnknown})</span>
                ) : f.d365Ncrs.length ? (
                  <span className="font-semibold text-(--warn)">
                    {f.d365Ncrs.map((n) => `${n.ncr}${n.problem ? ` ${n.problem}` : ''} (${n.closed ? 'closed' : 'open'}${n.date ? `, ${day(n.date)}` : ''})`).join('; ')}
                  </span>
                ) : (
                  'no D365 NCR on this batch'
                )}
                <span className="block text-[11px] text-(--muted)">D365 NCRs raised with a batch, from INVENTORYV2 as refreshed; one raised on the item alone is not found.</span>
              </dd>
              <dt className="font-semibold">Other cases on this batch</dt>
              <dd className="m-0">
                {f.otherCases.length
                  ? f.otherCases.map((id, i) => (
                      <span key={id}>
                        {i ? ', ' : ''}
                        <Link to={`/office/complaints/${id}`} className={`inline-flex min-h-[44px] items-center font-semibold underline sm:min-h-0 ${officeFocus}`}>
                          {caseNo(id)}
                        </Link>
                      </span>
                    ))
                  : 'none'}
              </dd>
            </dl>
          ) : null}

          {/* An inferred batch is confirmed with one press: listing it again under itself was noise (UI/UX pass, 2 Nov 2026). */}
          {b.state === 'INFERRED' && b.item && b.batch && pickable ? (
            <Button size="sm" type="button" className="min-h-[44px] self-start sm:min-h-0" disabled={busy} onClick={() => void name(b.item!, b.batch!)}>
              Confirm batch {b.batch}
            </Button>
          ) : null}
          {b.state === 'CANDIDATES' ? (
            <div className="flex flex-col gap-(--s2)">
              <span className={labelClass}>Which batch is it?</span>
              <ul className="m-0 flex list-none flex-col gap-(--s1) p-0 text-(length:--fs-sm)">
                {b.candidates.map((c) => (
                  <li key={`${c.item}-${c.batch}`} className="flex flex-wrap items-center gap-(--s2)">
                    <span className="tabular-nums">
                      <strong>{c.batch}</strong> · {c.itemName ?? c.item} ({c.item}) · made {c.made ? day(c.made) : 'not known'} · BB{' '}
                      {c.bestBefore ? day(c.bestBefore) : 'not known'}
                    </span>
                    {pickable ? (
                      <Button size="sm" variant="outline" type="button" className="min-h-[44px] sm:min-h-0" disabled={busy} onClick={() => void name(c.item, c.batch)}>
                        This one
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {pickable ? (
            showOther ? (
              <form
                className="flex flex-wrap items-end gap-(--s2)"
                onSubmit={(e) => {
                  e.preventDefault();
                  void name(other.item.trim(), other.batch.trim());
                }}
              >
                <label className="flex flex-col gap-1 text-(length:--fs-sm)">
                  <span className={labelClass}>D365 item</span>
                  <input className={`${fieldClass} ${officeFocus} w-28`} inputMode="numeric" value={other.item} onChange={(e) => setOther({ ...other, item: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1 text-(length:--fs-sm)">
                  <span className={labelClass}>Batch</span>
                  <input className={`${fieldClass} ${officeFocus} w-32`} value={other.batch} onChange={(e) => setOther({ ...other, batch: e.target.value })} />
                </label>
                <Button size="sm" type="submit" disabled={busy || !other.item.trim() || !other.batch.trim()}>
                  Name this batch
                </Button>
                <Button size="sm" variant="outline" type="button" onClick={() => setShowOther(false)}>
                  Cancel
                </Button>
              </form>
            ) : (
              <button type="button" className={`inline-flex min-h-[44px] items-center self-start text-(length:--fs-sm) underline sm:min-h-0 ${officeFocus}`} onClick={() => setShowOther(true)}>
                {b.batch ? 'Wrong batch? Name another' : 'Name the batch yourself'}
              </button>
            )
          ) : null}
          {error ? (
            <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
              {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
